import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Forklift, LicenceIcon } from '../../../../../../assets/icons';
import { parseKycReason } from '../../../../../../helper/kycDocumentReason';
import {
  LICENCE_VALIDATION,
  fillTemplate,
  flattenLicenceCatalogue,
  formatDay,
  getLicenceLabel,
  getLicenceOptionKey,
  getLicenceStatusBadge,
  isCacesLicence,
  isLicencePending,
} from '../../../../../../helper/licenceValidation';
import {
  calendarDateToLocalDate,
  formatCalendarDate,
  toCalendarDateString,
} from '../../../../../../helper/visaComplementaryDocuments';
import UseContext from '../../../../../../hooks/useContext';
import ApiDatabase from '../../../../../../server';
import { isSessionExpiredError } from '../../../../../../utils/authError';
import Banner from '../../../../../banner/banner';
import Divider from '../../../../../divider/divider';
import ModalBody from '../../../../components/body';
import UseModal from '../../../../useModal';
import {
  DateInput,
  DocumentSidebar,
  DocumentViewer,
  ModalHeader,
  UserInfoGrid,
  ValidationActions,
} from '../shared';
import useLicenceDocuments from './useLicenceDocuments';

const ADMIN_TYPE = 'licence';

const ERROR_KEYS = {
  LICENCE_EXPIRATION_REQUIRED: 'expirationRequired',
  LICENCE_NOT_PENDING: 'notPending',
  LICENCE_NOT_IN_CATALOGUE: 'notInCatalogue',
  LICENCE_NOT_FOUND: 'notFound',
  TEMP_WORKER_NOT_FOUND: 'tempWorkerNotFound',
};

const SIDEBAR_BADGE_CLASSES = {
  [LICENCE_VALIDATION.PENDING]: 'bg-yellow-100 text-yellow-700',
  [LICENCE_VALIDATION.ACCEPTED]: 'bg-green-100 text-green-700',
  [LICENCE_VALIDATION.REFUSED]: 'bg-red-100 text-red-700',
};
const REPLACED_BADGE_CLASS = 'bg-gray-100 text-gray-600';

// Colours of the AI result, read from the prefix the api writes (utils/documentUtils.js).
const AI_BANNER_CLASSES = {
  validated: 'bg-green-50 border-green-200 text-green-800',
  pending: 'bg-amber-50 border-amber-200 text-amber-800',
  rejected: 'bg-red-50 border-red-200 text-red-800',
  unknown: 'bg-gray-50 border-gray-200 text-gray-800',
};

const OBJECT_ID = /^[a-f0-9]{24}$/i;

// identity.nationality may come as a nationalities _id, a document or a name:
// only a readable name is shown.
const formatNationality = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return value.NationaliteM || value.name || '';
  }
  const text = String(value).trim();
  return OBJECT_ID.test(text) ? '' : text;
};

// The title the entry starts with: its own, then the one the AI read.
const getInitialTitle = (entry) => {
  const ai = entry?.ai || {};
  if (entry?.licence) {
    const category =
      entry.category ||
      (ai.licence === entry.licence ? ai.category || null : null);
    return { licence: entry.licence, category };
  }
  return { licence: ai.licence || '', category: ai.category || null };
};

const AdminModalFileAcceptLicence = () => {
  const { toggle, switchModal } = UseModal();
  const {
    adminTempWorkerId,
    adminUserId,
    licenceVerificationTempWorker,
    licenceVerificationLicence,
    setLicenceVerificationLicence,
    setLicenceVerificationDecision,
    setAdminType,
    language,
  } = UseContext();
  const token = localStorage.getItem('xsrfToken');
  const tempWorkerId =
    licenceVerificationTempWorker?.tempWorkerId || adminTempWorkerId;
  const userId = licenceVerificationTempWorker?.userId || adminUserId;

  const [details, setDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [catalogue, setCatalogue] = useState([]);
  const [selectedId, setSelectedId] = useState(
    licenceVerificationLicence?._id ? String(licenceVerificationLicence._id) : null,
  );
  const [selectionKey, setSelectionKey] = useState('');
  const [expiration, setExpiration] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const text = (key, fallback = '') =>
    language.getValue(`licenceValidation.${key}`, fallback);

  const showError = (err) => {
    setIsSubmitting(false);
    if (err?.name === 'CanceledError') return;
    // The api layer already signs the admin out on a real session failure.
    if (isSessionExpiredError(err)) return;
    const key = ERROR_KEYS[err?.response?.data?.errorCode] || 'generic';
    toast.error(text(`errors.${key}`));
  };

  useEffect(() => {
    if (!tempWorkerId) return;
    setIsLoadingDetails(true);
    ApiDatabase.getLicencesOfTempWorker(
      { tempWorkerId },
      (response) => {
        setDetails(response?.data ?? null);
        setIsLoadingDetails(false);
      },
      (err) => {
        setIsLoadingDetails(false);
        if (err?.name === 'CanceledError' || isSessionExpiredError(err)) return;
        const key = ERROR_KEYS[err?.response?.data?.errorCode] || 'loadFailed';
        toast.error(text(`errors.${key}`));
      },
    );
  }, [tempWorkerId]);

  useEffect(() => {
    ApiDatabase.getAllLicence(
      (data) => setCatalogue(Array.isArray(data) ? data : []),
      (err) => {
        if (err?.name === 'CanceledError' || isSessionExpiredError(err)) return;
        toast.error(text('errors.catalogueFailed'));
      },
      { abortKey: 'licence/ad_file_accept_licence' },
    );
  }, []);

  // Until the temp worker is loaded, the entry opened from the list stands alone.
  const entries = useMemo(() => {
    if (!details) {
      return licenceVerificationLicence?._id
        ? [{ entry: licenceVerificationLicence, isHistory: false }]
        : [];
    }
    return [
      ...(details.licences ?? []).map((entry) => ({ entry, isHistory: false })),
      ...(details.licencesHistory ?? []).map((entry) => ({
        entry,
        isHistory: true,
      })),
    ];
  }, [details, licenceVerificationLicence]);

  const selected = useMemo(
    () =>
      entries.find(({ entry }) => String(entry?._id) === String(selectedId)) ??
      null,
    [entries, selectedId],
  );
  const entry = selected?.entry ?? null;
  const isReadOnly = !entry || selected.isHistory || !isLicencePending(entry);

  useEffect(() => {
    const title = getInitialTitle(entry);
    setSelectionKey(
      title.licence ? getLicenceOptionKey(title.licence, title.category) : '',
    );
    setExpiration(
      calendarDateToLocalDate(entry?.expiration ?? entry?.ai?.expiration),
    );
  }, [entry]);

  const licenceIds = useMemo(
    () => entries.map(({ entry: item }) => String(item._id)),
    [entries],
  );
  const { docsById } = useLicenceDocuments({ token, userId, licenceIds });
  const docs = (selectedId && docsById[selectedId]) || [];
  const isWaitingDocs = !!selectedId && !docsById[selectedId];

  const options = useMemo(
    () => flattenLicenceCatalogue(catalogue, language),
    [catalogue, language],
  );
  const selectedOption = options.find((option) => option.key === selectionKey);

  // A decided or replaced entry may carry a title the catalogue no longer has:
  // it is still shown, never offered.
  const readOnlyOption =
    isReadOnly && !selectedOption && entry?.licence
      ? {
          key: selectionKey,
          label: getLicenceLabel(getInitialTitle(entry), language),
        }
      : null;

  const isCaces = isCacesLicence(selectedOption?.licence);
  const expirationDay = toCalendarDateString(expiration);
  const isMissingCacesDate = isCaces && !expirationDay;
  const canAccept =
    !isReadOnly && !!selectedOption && !isMissingCacesDate && !isSubmitting;

  const sidebarGroups = entries
    .filter(({ entry: item }) => String(item._id) !== String(selectedId))
    .map(({ entry: item, isHistory }) => {
      const id = String(item._id);
      const status = getLicenceStatusBadge(item.validation, language);
      return {
        id,
        type: ADMIN_TYPE,
        label: getLicenceLabel(item, language),
        docs: docsById[id] || [],
        badge: isHistory
          ? { label: text('modal.replaced'), className: REPLACED_BADGE_CLASS }
          : {
              label: status.label,
              className:
                SIDEBAR_BADGE_CLASSES[Number(item.validation)] ||
                SIDEBAR_BADGE_CLASSES[LICENCE_VALIDATION.PENDING],
            },
        onSelect: () => setSelectedId(id),
      };
    });

  const pagesKey =
    docs.length === 2 ? 'double' : docs.length > 2 ? 'many' : 'single';
  const isCacesEntry = isCacesLicence(entry?.licence);
  const headerSubtitle = entry
    ? fillTemplate(text('modal.subtitle'), {
        licence: getLicenceLabel(entry, language),
        pages: text(`modal.pages.${pagesKey}`),
      })
    : '';

  const user = {
    lastname: details?.lastname ?? licenceVerificationTempWorker?.lastname,
    firstname: details?.firstname ?? licenceVerificationTempWorker?.firstname,
    birthday: formatCalendarDate(details?.birthday),
  };

  const aiReason = entry?.ai?.reason;
  const aiResult = aiReason ? parseKycReason(aiReason) : null;

  const readOnlyMessage = (() => {
    if (!entry || !isReadOnly) return '';
    if (selected.isHistory) {
      const date = formatDay(entry.replacedAt);
      return date
        ? fillTemplate(text('modal.readOnly.replaced'), { date })
        : text('modal.readOnly.replacedNoDate');
    }
    if (Number(entry.validation) === LICENCE_VALIDATION.ACCEPTED) {
      return text('modal.readOnly.accepted');
    }
    return entry.reason
      ? fillTemplate(text('modal.readOnly.refused'), { reason: entry.reason })
      : text('modal.readOnly.refusedNoReason');
  })();

  const handleAccept = () => {
    if (!canAccept) return;
    setIsSubmitting(true);
    const licenceId = String(entry._id);
    ApiDatabase.postAcceptLicence(
      {
        tempWorkerId,
        licenceId,
        licence: selectedOption.licence,
        category: selectedOption.category ?? null,
        expiration: expirationDay || null,
      },
      (response) => {
        setIsSubmitting(false);
        toast.success(text('toasts.accepted'));
        setLicenceVerificationDecision({
          tempWorkerId,
          licenceId,
          action: 'accept',
          licence: response?.data,
        });
        toggle();
      },
      showError,
    );
  };

  // The refuse modal reads the entry from the context and comes back here.
  const handleRefuse = () => {
    setLicenceVerificationLicence(entry);
    setAdminType(ADMIN_TYPE);
    switchModal('ad_file_refuse');
  };

  return (
    <ModalBody>
      <div className="flex gap-4 h-[750px]">
        <DocumentSidebar
          allDocuments={sidebarGroups}
          isLoading={isLoadingDetails}
          currentType={ADMIN_TYPE}
          tempWorker={null}
          title={text('modal.otherDocuments')}
          onOpenDocumentModal={() => {}}
        />

        <div className="flex-1 min-w-0 bg-gray-50 rounded-lg overflow-hidden">
          {/* Keyed by entry: the sidebar swaps entries without remounting the
              modal, and the viewer keeps its page index and rotation otherwise. */}
          <DocumentViewer
            key={selectedId || 'none'}
            docs={docs}
            isWaiting={isWaitingDocs}
            allowRotation
            rotateLabel={text('modal.rotate')}
          />
        </div>

        <div className="w-80 flex flex-col gap-3 bg-gray-50 rounded-lg p-4 overflow-y-auto">
          <ModalHeader
            adminType={ADMIN_TYPE}
            docsCount={docs.length}
            onClose={toggle}
            label={text(isCacesEntry ? 'modal.caces' : 'modal.drivingLicence')}
            subtitle={headerSubtitle}
            icon={
              isCacesEntry ? (
                <Forklift wh={24} color="#6B7280" />
              ) : (
                <LicenceIcon wh={24} color="#6B7280" />
              )
            }
          />

          {!isLoadingDetails && !entry && (
            <Banner type="alert">{text('modal.notFound')}</Banner>
          )}

          {readOnlyMessage && (
            <Banner
              type={
                Number(entry?.validation) === LICENCE_VALIDATION.ACCEPTED &&
                !selected?.isHistory
                  ? 'info'
                  : 'alert'
              }
            >
              {readOnlyMessage}
            </Banner>
          )}

          {aiResult && (
            <div
              className={`p-3 border rounded-lg ft-sm ${
                AI_BANNER_CLASSES[aiResult.outcome]
              }`}
              data-testid="licence-ai-result"
              data-outcome={aiResult.outcome}
            >
              <div className="font-medium mb-1">{text('modal.aiTitle')}</div>
              <div className="whitespace-pre-wrap">
                {aiResult.text || aiReason}
              </div>
            </div>
          )}

          <UserInfoGrid
            user={user}
            showNationality
            nationalityText={formatNationality(details?.nationality)}
          />

          {entry && (
            <>
              <Divider />

              <label className="ft-sm text-gray-500 w-full">
                <div>{text('modal.licenceLabel')}</div>
                <select
                  className="w-full h-10 px-3 ft-b border rounded-lg text-gray-900 bg-white disabled:bg-gray-100"
                  value={selectedOption ? selectionKey : readOnlyOption?.key ?? ''}
                  onChange={(e) => setSelectionKey(e.target.value)}
                  disabled={isReadOnly}
                >
                  <option value="">{text('modal.licencePlaceholder')}</option>
                  {readOnlyOption && (
                    <option value={readOnlyOption.key}>
                      {readOnlyOption.label}
                    </option>
                  )}
                  {options.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {isReadOnly ? (
                <div className="grid grid-cols-2 gap-4 ft-sm">
                  <div>{text('modal.expirationLabel')}</div>
                  <div className="text-gray-500">
                    {formatCalendarDate(entry.expiration) || '—'}
                  </div>
                </div>
              ) : (
                <div>
                  <DateInput
                    label={text('modal.expirationLabel')}
                    value={expiration}
                    onChange={setExpiration}
                    clearLabel={text('modal.clearExpiration')}
                  />
                  {isMissingCacesDate && (
                    <div className="mt-1 text-xs text-amber-700">
                      {text('modal.expirationRequiredHint')}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <ValidationActions
            onRefuse={handleRefuse}
            onAccept={handleAccept}
            showAccept={!isReadOnly}
            showRefuse={!isReadOnly}
            isAcceptDisabled={!canAccept}
            acceptLabel={text('modal.accept')}
            refuseLabel={text('modal.refuse')}
          />
        </div>
      </div>
    </ModalBody>
  );
};

export default AdminModalFileAcceptLicence;
